// Industry-specific landing pages at /for/{slug}. Built for high-intent Google
// searches like "financial analysis tool for restaurants", "SaaS health check",
// "construction business ratios", etc. Each page is a dedicated SEO target.
//
// industryKey maps to /app/backend/benchmarks.py keys so the Analyze form
// pre-fills the right industry on CTA.

export const FOR_INDUSTRIES = {
  restaurants: {
    sub: "Restaurants & food service",
    industryKey: "restaurant",
    headline: "Financial health for restaurants — graded.",
    sub_headline:
      "Prime cost, food cost, labor cost — the four numbers that decide whether your restaurant survives. CFO Grade reads your P&L and tells you exactly where you stand.",
    seoTitle:
      "Restaurant Financial Health Tool — Free Letter Grade & Industry Benchmarks | CFO Grade",
    seoDescription:
      "Free financial health check for restaurant owners. Paste your P&L and get prime cost, food cost, labor cost, EBITDA, and a letter grade against restaurant industry benchmarks.",
    benchmark: [
      { k: "Food cost %", v: "28–32%", learn: "food-cost" },
      { k: "Labor cost %", v: "28–35%", learn: "labor-cost" },
      { k: "Prime cost", v: "≤ 60%", learn: "prime-cost" },
      { k: "EBITDA margin", v: "10–15%", learn: "ebitda-margin" },
    ],
    painPoints: [
      "Your prime cost looks fine, but cash is always tight.",
      "Banks ask for a DSCR and you don't know what yours is.",
      "You feel busy — but never sure if you're actually profitable.",
    ],
    deliverables: [
      "Prime cost grade — A+ to F — against restaurant medians",
      "Food cost & labor cost breakdown vs industry benchmark",
      "DSCR check (the number SBA lenders care about most)",
      "Plain-English CFO memo of what to fix next quarter",
    ],
  },
  saas: {
    sub: "SaaS & software",
    industryKey: "saas",
    headline: "The financial health check every SaaS founder should run before a raise.",
    sub_headline:
      "Rule of 40, burn multiple, runway, gross margin — get the metrics a credit committee or VC analyst would compute, in one click.",
    seoTitle:
      "SaaS Financial Health Tool — Rule of 40, Burn Multiple, Runway | CFO Grade",
    seoDescription:
      "Free SaaS financial health analyzer. Paste your P&L and instantly see your Rule of 40, burn multiple, runway, gross margin, and letter grade — with industry benchmarks.",
    benchmark: [
      { k: "Gross margin (SaaS)", v: "70–85%", learn: "gross-margin" },
      { k: "Rule of 40", v: "≥ 40", learn: "rule-of-40" },
      { k: "Burn multiple", v: "< 1.5×", learn: "burn-multiple" },
      { k: "Runway (months)", v: "≥ 18", learn: "runway" },
    ],
    painPoints: [
      "You can recite ARR but not your Rule of 40.",
      "Burn multiple is rising and you don't know if that's normal.",
      "Need a clean analyst-tier readout for board / investors.",
    ],
    deliverables: [
      "Rule of 40 calculation against SaaS medians",
      "Burn multiple and runway",
      "Analyst-lens memo (think credit committee, not pitch deck)",
      "PDF for investors & board",
    ],
  },
  ecommerce: {
    sub: "E-commerce & DTC",
    industryKey: "ecommerce",
    headline: "Shopify shows you revenue. We show you whether you're making money.",
    sub_headline:
      "Contribution margin, cash conversion cycle, inventory turns. The metrics an acquirer or aggregator would actually pay attention to.",
    seoTitle:
      "E-commerce Financial Health Tool — Margins, Cash Conversion, Letter Grade | CFO Grade",
    seoDescription:
      "Free financial health check for Shopify and DTC stores. See your contribution margin, cash conversion cycle, inventory turns, and letter grade against e-commerce benchmarks.",
    benchmark: [
      { k: "Gross margin", v: "40–60%", learn: "gross-margin" },
      { k: "Net margin", v: "5–12%", learn: "net-margin" },
      { k: "Inventory days", v: "60–90", learn: "dio" },
      { k: "Cash conv. cycle", v: "< 60 days", learn: "cash-conversion-cycle" },
    ],
    painPoints: [
      "Sales are up but cash is somehow tighter than ever.",
      "Inventory is eating all your working capital.",
      "Need numbers an aggregator or buyer would underwrite from.",
    ],
    deliverables: [
      "Contribution margin and cash conversion cycle",
      "Inventory turn vs DTC industry medians",
      "Aggregator-friendly summary metrics",
      "PDF for broker conversations",
    ],
  },
  construction: {
    sub: "Construction & trades",
    industryKey: "construction",
    headline: "DSCR, working capital, bonding — graded in one click.",
    sub_headline:
      "Construction businesses look fine on revenue and break on cash flow. Get the numbers your bank and surety will judge you on, in seconds.",
    seoTitle:
      "Construction Financial Health Tool — DSCR, Working Capital, Bonding | CFO Grade",
    seoDescription:
      "Free financial health analyzer for construction businesses and trades. See your DSCR, working capital, debt coverage, and letter grade against construction industry benchmarks.",
    benchmark: [
      { k: "Gross margin", v: "15–25%", learn: "gross-margin" },
      { k: "Net margin", v: "3–7%", learn: "net-margin" },
      { k: "DSCR (for bonding)", v: "≥ 1.5×", learn: "dscr" },
      { k: "Current ratio", v: "≥ 1.5×", learn: "current-ratio" },
    ],
    painPoints: [
      "Bonding capacity is being held back and you don't know why.",
      "WIP and retention are eating your working capital.",
      "Need a quick DSCR check before applying for a credit line.",
    ],
    deliverables: [
      "DSCR check tuned for construction sureties (≥ 1.5×)",
      "Working capital and current ratio vs trade medians",
      "Plain-English memo: what to fix before applying",
      "PDF for your surety or banker",
    ],
  },
  professional_services: {
    sub: "Professional services",
    industryKey: "professional_services",
    headline: "A 60-second financial health check for your firm.",
    sub_headline:
      "Margins, utilization, debt coverage, cash cycle — see how your practice stacks up against other accounting, legal, and consulting firms.",
    seoTitle:
      "Professional Services Financial Health Tool — Margins, DSCR | CFO Grade",
    seoDescription:
      "Free financial health check for accounting, legal, and consulting firms. See your margins, DSCR, cash cycle, and letter grade against industry benchmarks.",
    benchmark: [
      { k: "Gross margin", v: "40–60%", learn: "gross-margin" },
      { k: "EBITDA margin", v: "15–25%", learn: "ebitda-margin" },
      { k: "DSO (collections)", v: "30–45 days", learn: "dso" },
      { k: "Net margin", v: "10–18%", learn: "net-margin" },
    ],
    painPoints: [
      "Collections are dragging and tying up cash.",
      "Margins look fine — but partners aren't getting paid.",
      "Need a quick sanity check before partner draws.",
    ],
    deliverables: [
      "Margins and DSO vs professional-services medians",
      "DSCR check for SBA loans and lines of credit",
      "Owner-lens memo of what to fix this quarter",
      "PDF for partner meetings",
    ],
  },
  healthcare_practice: {
    sub: "Healthcare practice",
    industryKey: "healthcare_practice",
    headline: "How does your practice compare to other healthcare businesses?",
    sub_headline:
      "Dental, medical, vet — get a financial health read tuned to private-practice economics, with industry-specific benchmarks.",
    seoTitle:
      "Medical / Dental Practice Financial Tool — Free Letter Grade | CFO Grade",
    seoDescription:
      "Free financial health analyzer for dental, medical, and veterinary practices. See your margins, DSCR, and letter grade against healthcare-practice benchmarks.",
    benchmark: [
      { k: "Gross margin", v: "40–55%", learn: "gross-margin" },
      { k: "EBITDA margin", v: "20–35%", learn: "ebitda-margin" },
      { k: "DSCR", v: "≥ 1.25×", learn: "dscr" },
      { k: "Current ratio", v: "≥ 1.5×", learn: "current-ratio" },
    ],
    painPoints: [
      "Production is up but the bank account isn't.",
      "Thinking about adding chairs / a second location and need to vet the numbers.",
      "DSCR check before a practice acquisition loan.",
    ],
    deliverables: [
      "Healthcare-practice-specific benchmarks",
      "DSCR check for practice-acquisition loans",
      "Margin trend tracking quarter over quarter",
      "PDF for your banker",
    ],
  },
  manufacturing: {
    sub: "Manufacturing",
    industryKey: "manufacturing",
    headline: "Capacity, margin, leverage — a 60-second financial health check.",
    sub_headline:
      "Manufacturing lives on thin margins and heavy assets. Get a quick read on whether you're earning your cost of capital — with industry-specific benchmarks.",
    seoTitle:
      "Manufacturing Financial Health Tool — Free Letter Grade & Benchmarks | CFO Grade",
    seoDescription:
      "Free financial analysis for manufacturing businesses. See your gross margin, DSCR, debt-to-EBITDA, inventory turnover, and letter grade against manufacturing benchmarks.",
    benchmark: [
      { k: "Gross margin", v: "20–35%", learn: "gross-margin" },
      { k: "EBITDA margin", v: "10–18%", learn: "ebitda-margin" },
      { k: "Inventory turn", v: "4–8×/yr", learn: "inventory-turnover" },
      { k: "Debt / EBITDA", v: "< 3.0×", learn: "debt-to-ebitda" },
    ],
    painPoints: [
      "Margins look fine on paper but cash never seems to grow.",
      "Inventory and WIP are eating all the working capital.",
      "Need a DSCR check before refinancing the equipment loan.",
    ],
    deliverables: [
      "Manufacturing-specific margins and inventory turn",
      "DSCR and Debt / EBITDA for capex lenders",
      "Plain-English memo of what to fix next quarter",
      "PDF for your banker or board",
    ],
  },
  retail: {
    sub: "Retail",
    industryKey: "retail_general",
    headline: "Real margins. Real cash cycle. Not just same-store sales.",
    sub_headline:
      "Retail looks healthy until rent and inventory both start eating cash. CFO Grade reads your numbers and flags where you're really leaking margin.",
    seoTitle:
      "Retail Financial Health Tool — Margins, Cash Cycle, Letter Grade | CFO Grade",
    seoDescription:
      "Free financial health analyzer for retail businesses. Get your gross margin, inventory turnover, cash conversion cycle, and letter grade against retail industry benchmarks.",
    benchmark: [
      { k: "Gross margin", v: "30–50%", learn: "gross-margin" },
      { k: "Inventory turn", v: "4–8×/yr", learn: "inventory-turnover" },
      { k: "Cash conv. cycle", v: "< 60 days", learn: "cash-conversion-cycle" },
      { k: "Net margin", v: "3–8%", learn: "net-margin" },
    ],
    painPoints: [
      "Sales are flat and rent keeps going up.",
      "Inventory keeps growing faster than sales.",
      "Need to know whether the second location is a real opportunity.",
    ],
    deliverables: [
      "Retail-specific inventory turn and cash cycle",
      "DSCR check before a credit line draw",
      "PDF for landlord, banker, or partner",
      "Plain-English memo on margin protection",
    ],
  },
  agriculture: {
    sub: "Agriculture",
    industryKey: "agriculture",
    headline: "Yields up. Cash down? Find out exactly where it's going.",
    sub_headline:
      "Farms, ranches, growers — equipment, fuel, and weather risk make this industry uniquely sensitive to leverage and working capital. Get graded.",
    seoTitle:
      "Agriculture Financial Health Tool — Free Letter Grade | CFO Grade",
    seoDescription:
      "Free financial analyzer for agricultural businesses. See your margins, DSCR, debt-to-EBITDA, working capital, and letter grade against ag-industry benchmarks.",
    benchmark: [
      { k: "Gross margin", v: "20–40%", learn: "gross-margin" },
      { k: "DSCR", v: "≥ 1.25×", learn: "dscr" },
      { k: "Current ratio", v: "≥ 1.5×", learn: "current-ratio" },
      { k: "Debt / Assets", v: "< 50%", learn: "debt-to-assets" },
    ],
    painPoints: [
      "Equipment debt and operating line keep stacking up.",
      "Need a DSCR check before the spring operating loan renewal.",
      "Not sure if your debt-to-assets is healthy or alarming.",
    ],
    deliverables: [
      "DSCR and Debt-to-Assets check for ag lenders",
      "Working capital read on operating-line health",
      "PDF for your ag lender or FSA review",
      "Plain-English memo on which costs to attack",
    ],
  },
  transport: {
    sub: "Transportation & logistics",
    industryKey: "transportation",
    headline: "Per-mile economics, in plain English.",
    sub_headline:
      "Trucking, delivery, fleet — fuel and labor swing margins fast. Get a quick read on your real profitability with transportation industry benchmarks.",
    seoTitle:
      "Trucking / Logistics Financial Tool — Free Letter Grade | CFO Grade",
    seoDescription:
      "Free financial analysis for trucking, logistics, and transportation businesses. See your operating margin, DSCR, fleet leverage, and letter grade against industry benchmarks.",
    benchmark: [
      { k: "Operating margin", v: "5–12%", learn: "operating-margin" },
      { k: "EBITDA margin", v: "12–20%", learn: "ebitda-margin" },
      { k: "DSCR (for fleet loans)", v: "≥ 1.3×", learn: "dscr" },
      { k: "Debt / EBITDA", v: "< 3.5×", learn: "debt-to-ebitda" },
    ],
    painPoints: [
      "Fuel and driver costs are eating margins.",
      "Equipment loan stack is getting heavy.",
      "DSCR check before refinancing or adding a tractor.",
    ],
    deliverables: [
      "Transportation-specific operating margin and EBITDA",
      "DSCR / Debt-to-EBITDA for fleet-loan lenders",
      "Cash cycle read on factoring or net-30 customers",
      "PDF for your fleet lender",
    ],
  },
  hospitality: {
    sub: "Hospitality & lodging",
    industryKey: "hospitality_lodging",
    headline: "RevPAR is up. Are you profitable?",
    sub_headline:
      "Hotels, B&Bs, vacation rentals — revenue metrics are necessary but not sufficient. Get the financial read your bank actually cares about.",
    seoTitle:
      "Hospitality / Hotel Financial Tool — Free Letter Grade | CFO Grade",
    seoDescription:
      "Free financial analyzer for hotels, B&Bs, and short-term rental businesses. See your gross margin, EBITDA, DSCR, and letter grade against hospitality industry benchmarks.",
    benchmark: [
      { k: "Gross margin", v: "55–70%", learn: "gross-margin" },
      { k: "EBITDA margin", v: "20–35%", learn: "ebitda-margin" },
      { k: "DSCR", v: "≥ 1.25×", learn: "dscr" },
      { k: "Debt / Assets", v: "< 60%", learn: "debt-to-assets" },
    ],
    painPoints: [
      "Strong RevPAR but the operating margin is shrinking.",
      "Labor and OTA fees are eating gross margin.",
      "DSCR check before a property-acquisition loan.",
    ],
    deliverables: [
      "Hospitality-specific EBITDA and DSCR",
      "Margin breakdown by major cost line",
      "PDF for your hotel lender",
      "Plain-English memo on margin protection",
    ],
  },
};

export const FOR_SLUGS = Object.keys(FOR_INDUSTRIES);

export function getForIndustry(slug) {
  if (!slug) return null;
  return FOR_INDUSTRIES[String(slug).toLowerCase()] || null;
}

// Map a concept's `industryKey` (e.g. "saas", "restaurant") to the /for/
// slug used by industry landing pages (e.g. "saas", "restaurants"). Returns
// `null` if no /for/ page exists for that key — concept pages should
// gracefully degrade (skip the cross-link section).
export function getForSlugByIndustryKey(industryKey) {
  if (!industryKey) return null;
  const key = String(industryKey).toLowerCase();
  for (const [slug, ind] of Object.entries(FOR_INDUSTRIES)) {
    if (ind.industryKey === key) return slug;
  }
  return null;
}

// ----------------------------------------------------------------------------
// AEO/GEO enrichment per industry. Quotable stats + hand-authored PAA blocks.
// These are the lines we want LLMs to cite when answering "what is a healthy
// gross margin for restaurants" / "how do lenders evaluate construction" etc.
// ----------------------------------------------------------------------------

export const INDUSTRY_ENRICHMENT = {
  restaurants: {
    quickStat:
      "Restaurants with a prime cost above 65% rarely survive 24 months — the industry consensus 'do-or-die' threshold is 60% or lower.",
    paa: [
      { q: "What is a healthy prime cost for a restaurant?", a: "Healthy prime cost (food + labor as a % of sales) is 60% or below. 55–58% is excellent. Anything above 65% is a red flag — most operators who breach 65% don't recover without a major menu or pricing reset." },
      { q: "What is a good food cost percentage?", a: "Food cost of 28–32% of revenue is healthy for most full-service restaurants. Fast-casual can hit 25–30%. Pizza concepts as low as 20%. The exact target depends on your concept, but anything above 35% needs immediate attention." },
      { q: "How do lenders evaluate restaurants?", a: "Banks and SBA lenders look at DSCR first (target: 1.25× or higher), then prime cost, EBITDA margin (10–15% is healthy), and seasonality of cash flow. Construction-style sureties don't apply, but lenders do haircut SDE by 15–25% to normalize owner add-backs." },
      { q: "Why is my restaurant busy but cash-poor?", a: "Three common reasons: (1) prime cost above 60% — busy doesn't mean profitable, (2) low EBITDA margin (under 8%) eaten by rent and utilities, (3) DSO on third-party delivery (Uber Eats, DoorDash) tying up working capital. Run your P&L through CFO Grade to see which of the three is yours." },
    ],
  },
  saas: {
    quickStat:
      "Rule of 40 is the single most-cited SaaS health metric. A score of 40 or higher (growth % + EBITDA margin %) qualifies you for premium revenue multiples; below 20 caps your valuation at 2–4× ARR.",
    paa: [
      { q: "What is the Rule of 40?", a: "Rule of 40 = Revenue Growth Rate (%) + EBITDA Margin (%). It's the standard SaaS scorecard used by VCs and PE buyers. A score above 40 indicates the business is balancing growth and profitability efficiently; below 30 is a warning sign." },
      { q: "What is a good burn multiple?", a: "Burn multiple = Net Burn ÷ Net New ARR. Best-in-class is under 1.0× ('a dollar in, a dollar of new ARR'). 1.0–1.5× is good. 1.5–2.0× is acceptable in early years. Above 2.0× is concerning and typically requires a recap." },
      { q: "How much runway should a SaaS startup have?", a: "Minimum 18 months of runway is the venture standard — enough to ship the next milestone and run a 6-month fundraise without distraction. Less than 12 months and you're in 'raise immediately or die' territory." },
      { q: "What metrics do SaaS investors care about most?", a: "ARR, growth rate, gross margin (target: 75%+), Rule of 40, burn multiple, net dollar retention (110%+ is great), CAC payback (under 18 months), and runway. CFO Grade computes all of these from your P&L in one click." },
    ],
  },
  ecommerce: {
    quickStat:
      "DTC brands with a cash conversion cycle over 90 days fail at 3× the rate of those under 45 — inventory days are the silent killer of e-commerce businesses.",
    paa: [
      { q: "What financial metrics matter most for Shopify stores?", a: "Contribution margin, blended gross margin, inventory turn (target: 4–8× per year), cash conversion cycle (target: under 60 days), and CAC payback. Revenue alone tells you nothing — many 8-figure DTC brands are unprofitable at scale." },
      { q: "What is a good gross margin for e-commerce?", a: "40–60% blended gross margin is healthy for most DTC brands. Apparel and beauty often hit 60–75%. Electronics and accessories 30–40%. If your gross margin is below 35% after shipping costs, scaling will accelerate losses rather than profits." },
      { q: "How do aggregators value Shopify businesses?", a: "Aggregators typically pay 3–5× trailing twelve-month SDE (Seller's Discretionary Earnings), adjusted for owner add-backs. They focus on contribution margin, customer concentration, traffic source diversity, and inventory health. CFO Grade computes all the metrics they underwrite from." },
      { q: "Why is my Shopify store profitable on the P&L but cash-poor?", a: "Almost always working capital trapped in inventory or in-transit goods. Your P&L recognizes revenue on sale, but cash was spent months earlier on the inventory. Look at your cash conversion cycle — if it's above 75 days, that's where your money is sitting." },
    ],
  },
  construction: {
    quickStat:
      "Construction sureties typically require DSCR of 1.5× or higher to underwrite bonding capacity — meaningfully stricter than the 1.25× most commercial banks accept.",
    paa: [
      { q: "What DSCR do construction lenders require?", a: "Most commercial banks lending to construction companies require DSCR of 1.25× minimum. Sureties underwriting bonding capacity want 1.5× or higher because of project completion risk. SBA 7(a) accepts 1.15× for construction but with stricter working-capital covenants." },
      { q: "What is a healthy gross margin for construction?", a: "Gross margin of 15–25% is healthy for most general contractors and trades. Specialty trades (mechanical, electrical) can hit 30–35%. Bidding too thin (under 12%) leaves no margin for change orders and weather risk — a common cause of construction company failures." },
      { q: "Why is my construction business cash-poor despite a strong backlog?", a: "Retention and WIP. Retention (5–10% held back per draw) ties up cash for months. WIP not yet billed compounds the problem. A backlog that looks like $2M in revenue may have only $1.4M in collectible cash for the next 6 months. Working capital and current ratio are the metrics to watch." },
      { q: "How does bonding capacity relate to financial health?", a: "Bonding capacity is typically 10–20× working capital. To grow bonding capacity, sureties want to see: working capital above $250k for small contractors, DSCR above 1.5×, current ratio above 1.5×, and 3 years of clean financials. CFO Grade scores all four." },
    ],
  },
  professional_services: {
    quickStat:
      "Professional services firms with DSO above 60 days lose more to working-capital cost than they typically realize — the implicit interest rate is often 8–12%.",
    paa: [
      { q: "What is a good gross margin for a professional services firm?", a: "Accounting, legal, and consulting firms typically run gross margin of 40–60%. Software-augmented firms (RPA, audit tech) push 55–65%. Below 35% suggests over-staffing relative to billable hours, or pricing that hasn't kept pace with salaries." },
      { q: "What is a healthy DSO for professional services?", a: "Target DSO is 30–45 days. Above 60 means collections are leaking working capital and partners are effectively self-funding the firm. Above 90 days, write-offs are usually understated." },
      { q: "How do banks evaluate accounting and law firms?", a: "Banks look at DSCR (target: 1.25× or higher), EBITDA margin after owner comp (target: 15–25%), and DSO. They discount partner draws heavily and underwrite to normalized salaries. Lines of credit are common for collections-driven working capital." },
      { q: "How can I tell if my firm is profitable enough to support partner draws?", a: "Compute EBITDA after market-rate compensation for all partners (not after current draws). If EBITDA after normalization is positive, the firm is genuinely profitable. If it's only positive because partners are under-paying themselves, draws are unsustainable." },
    ],
  },
  healthcare_practice: {
    quickStat:
      "Private medical practices that breach DSCR 1.25× during equipment-loan acquisition typically take 18–24 months to recover — careful pre-deal modeling is the cheapest insurance you can buy.",
    paa: [
      { q: "What is a healthy EBITDA margin for a dental or medical practice?", a: "Mature private practices typically run EBITDA margin of 20–35% after market-rate provider compensation. Below 15% suggests over-investment in equipment, under-pricing, or insurance mix issues. Above 35% is excellent and supports a premium acquisition multiple." },
      { q: "How do banks evaluate practice acquisition loans?", a: "Banks underwrite practice acquisition loans on DSCR (1.25× minimum), historical collections (target: 30–45 days DSO), patient/provider retention, and post-acquisition projected EBITDA after debt service. They typically lend 80–100% of the purchase price for established practices." },
      { q: "Why is my practice's production up but the bank account isn't?", a: "Three usual suspects: (1) insurance mix shifting toward slower-paying carriers (DSO climbing), (2) equipment loan service ratcheting up, (3) new providers ramping but not yet at productivity. Look at DSO and DSCR — they tell the story fast." },
      { q: "What metrics should I track quarterly?", a: "Collections, DSO, gross margin (after lab/supply costs), EBITDA margin (after market-rate provider comp), DSCR, and active patient count. CFO Grade computes the financial side; practice management software handles the patient-side metrics." },
    ],
  },
  manufacturing: {
    quickStat:
      "US manufacturers with Debt/EBITDA above 3.5× face borrowing costs roughly 200–300 bps higher than peers under 3× — leverage discipline directly shapes cost of capital.",
    paa: [
      { q: "What is a healthy gross margin for manufacturing?", a: "Healthy gross margin is 20–35% for most manufacturers. Custom and specialty manufacturing can hit 35–45%. Commodity production runs 12–18%. Below 15% leaves no buffer for input-cost shocks and is a common cause of cyclical failures." },
      { q: "What inventory turn should manufacturers target?", a: "4–8 turns per year is healthy for most discrete manufacturers. Continuous process (food, chemicals) can hit 10–15. Below 3 turns typically signals slow-moving inventory, forecast issues, or production-pull mismatches. Each extra turn is real cash freed up." },
      { q: "How do banks evaluate manufacturing businesses?", a: "Banks focus on DSCR (1.25× target), Debt/EBITDA (under 3.0× preferred), gross margin trend, and inventory turn. Equipment loans are secured by collateral but the cash flow test still applies. ABL (asset-based lending) often comes into play above $5M in revenue." },
      { q: "Why is my P&L profitable but I can't cover payroll?", a: "Working capital trapped in inventory and AR. Manufacturing recognizes COGS when goods are sold, but cash was spent months earlier on raw materials and labor. If you can't make payroll, the diagnosis is almost always cash conversion cycle, not profitability." },
    ],
  },
  retail: {
    quickStat:
      "Retail stores where rent exceeds 10% of revenue rarely earn an EBITDA margin above 5% — occupancy cost is the second-largest predictor of retail failure after inventory turn.",
    paa: [
      { q: "What is a good gross margin for retail?", a: "Healthy gross margin is 30–50% for most general retail. Specialty boutiques can hit 50–60%. Big-box discount retail runs 22–30%. Below 25%, scale gains are limited because the unit economics are too thin to absorb shrink, returns, and markdowns." },
      { q: "What inventory turn should retailers target?", a: "4–8 turns per year is healthy depending on category. Apparel: 4–6. Grocery: 12–20. Furniture: 2–4. Below your category benchmark and inventory is sitting too long — markdowns and obsolescence costs will follow." },
      { q: "How is occupancy cost related to retail profitability?", a: "Occupancy cost (rent + CAM) under 8% of revenue is healthy. 8–10% is acceptable. Above 10% is a red flag — most retailers with double-digit occupancy cost run EBITDA margins under 5%. Negotiate or relocate before extending the lease." },
      { q: "Is a second location a good idea?", a: "Only if your current store hits: 4+ inventory turns, EBITDA margin above 10%, DSCR above 1.5×, and current ratio above 1.5×. Anything weaker and you're amplifying a problem, not solving it. CFO Grade will flag these four in your dashboard." },
    ],
  },
  agriculture: {
    quickStat:
      "FSA-guaranteed ag loans typically require Debt/Assets below 50% and DSCR above 1.25× — leverage discipline is more enforced in agriculture than in any other sector.",
    paa: [
      { q: "What DSCR do ag lenders require?", a: "Most ag lenders require DSCR of 1.25× minimum. FSA-guaranteed operating loans accept 1.15× but with stricter working-capital covenants. Production loans for high-input years (planting season) often require pre-approved DSCR projections rather than trailing actuals." },
      { q: "What is a healthy Debt/Assets ratio for a farm?", a: "Debt/Assets below 50% is considered healthy in agriculture. 50–65% is acceptable for growth phases. Above 65% materially raises borrowing cost and limits operating-line capacity. FSA and farm-credit lenders consider leverage discipline closely." },
      { q: "Why is my farm cash-poor despite high yields?", a: "Yields don't equal cash. Equipment debt, fuel, fertilizer, and seed get paid in spring; revenue arrives in fall. Operating-line interest accrues all season. If working capital and current ratio are weak, the cash gap widens every cycle." },
      { q: "How do I prepare financials for an operating-loan renewal?", a: "Lenders want: trailing 3 years of financials, current balance sheet, DSCR projection for the coming cycle, debt schedule, and a budget that ties to historical performance. CFO Grade builds the ratio analysis your lender will compute first." },
    ],
  },
  transport: {
    quickStat:
      "Trucking businesses with DSCR below 1.3× lose access to favorable fleet-loan terms — the rate spread between 1.3× and 1.5× DSCR borrowers is often 150–250 bps.",
    paa: [
      { q: "What is a good operating margin for trucking?", a: "Operating margin of 5–12% is healthy for most for-hire trucking businesses. Specialized hauling (oversized, hazmat) can hit 12–18%. Below 4% leaves no buffer for fuel spikes or driver shortages — common causes of trucking company failure." },
      { q: "What DSCR do fleet lenders require?", a: "Most equipment-finance lenders for fleets require DSCR of 1.3× or higher. Bank lines of credit secured by AR want 1.5×. Factor companies operate differently — they advance against invoices rather than underwrite DSCR." },
      { q: "How do I improve cash flow when customers pay net-60?", a: "Three options: (1) factor invoices (immediate cash, 2–4% fee), (2) tighten DSO via faster invoicing and early-pay incentives, (3) negotiate net-30 with strategic customers in exchange for rate concessions. Factoring is fastest but most expensive." },
      { q: "What metrics matter most for a trucking business?", a: "Operating margin per loaded mile, fuel cost as % of revenue, driver pay as % of revenue, DSCR, Debt/EBITDA, and cash conversion cycle. Per-mile economics tell you whether each load is profitable; the ratios tell you whether the whole business is sustainable." },
    ],
  },
  hospitality: {
    quickStat:
      "Hotels with labor cost above 35% of revenue rarely sustain a 25%+ EBITDA margin — labor discipline is the single largest controllable lever in lodging profitability.",
    paa: [
      { q: "What is a healthy EBITDA margin for a hotel?", a: "Healthy EBITDA margin is 20–35% for most independent and branded hotels. Limited-service brands often hit 30–40%. Full-service and resort properties 20–28%. Below 18% suggests labor, OTA fees, or property-tax costs are running hot." },
      { q: "How do hotel lenders evaluate financial health?", a: "Lenders look at DSCR (1.25× minimum, 1.4× for premium properties), Debt/Assets (under 60%), and stabilized EBITDA. Acquisition financing typically requires 12 months of stabilized operations or strong comp data for new builds." },
      { q: "Why is my RevPAR up but my profit down?", a: "Three common reasons: (1) ADR growth attracting higher OTA commission rates, (2) labor cost rising faster than rate, (3) property-tax reassessment based on the higher revenue. Look at EBITDA margin trend — if RevPAR climbed 8% but EBITDA margin fell 200 bps, you have a cost-structure problem." },
      { q: "What is the biggest financial risk for hotels?", a: "Concentration: of revenue (one corporate contract or OTA), of season (peak/off-peak swings), and of debt service (balloon payments). The most-cited cause of hotel failure is refinancing risk during a soft cycle, not operating losses." },
    ],
  },
};

export function getIndustryEnrichment(slug) {
  if (!slug) return null;
  return INDUSTRY_ENRICHMENT[String(slug).toLowerCase()] || null;
}
